import { useCallback, useEffect, useState } from 'react';

export type BYOProvider = 'openai' | 'anthropic' | 'gemini';

const STORAGE_KEY = 'clyra-byo-keys';

type KeyBag = Partial<Record<BYOProvider, string>>;

function read(): KeyBag {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed as KeyBag;
  } catch {
    // corrupt entry — ignore
  }
  return {};
}

function write(bag: KeyBag) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(bag));
}

/**
 * useBYOKeys manages per-provider API keys stored in localStorage.
 * Keys never leave the browser except in-flight on provider requests.
 */
export function useBYOKeys() {
  const [keys, setKeys] = useState<KeyBag>(read);

  // Keep tabs in sync — another tab editing keys should propagate.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setKeys(read());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setKey = useCallback((provider: BYOProvider, key: string) => {
    setKeys((prev) => {
      const next: KeyBag = { ...prev };
      const trimmed = key.trim();
      if (trimmed) next[provider] = trimmed;
      else delete next[provider];
      write(next);
      return next;
    });
  }, []);

  const clearKey = useCallback((provider: BYOProvider) => {
    setKey(provider, '');
  }, [setKey]);

  const getKey = useCallback((provider: BYOProvider) => keys[provider] ?? '', [keys]);
  const hasKey = useCallback((provider: BYOProvider) => Boolean(keys[provider]), [keys]);

  return { keys, setKey, clearKey, getKey, hasKey };
}
